// Resume handler for the ChiDesk deposit Paystack round-trip.
//
// When the customer returns from Paystack the URL carries
// `?vt_chidesk_ref=<reference>`. We pick the cart up from localStorage,
// poll the payments row until paystack-webhook flips it to 'success',
// then call bookChideskCart with the reference. The proxy verifies the
// reference itself before booking, so this layer is a UX wrapper — not
// the security boundary.
//
// PARTIAL BOOKING RESOLUTION: if some treatments book but another can't (a
// therapist grabbed the last slot between checkout and finalize), we do NOT
// leave it for manual reconciliation and do NOT silently re-time it. Instead
// the guest is shown the failed treatment's LIVE available times and decides:
// pick a new time (we re-book that leg against the deposit already paid) or
// cancel it (we refund that leg's deposit). See PartialResolver below.
import { useEffect, useRef, useState } from "react";
import {
  bookChideskCart,
  clearPendingChideskBooking,
  fetchChideskBookingConfig,
  fetchChideskDaySlots,
  getChideskPaymentStatus,
  loadPendingChideskBooking,
  refundPendingLegs,
  type ChideskBookingConfig,
  type ChideskBookItem,
  type ChideskDaySlots,
  type ChideskSlot,
  type PendingChideskBooking,
} from "../state/chidesk";
import {
  labelFromWallMinutes,
  normMinute,
  shiftIsoWallClock,
  wallMinutesOf,
} from "../state/chidesk-availability";
import { majorUnits, pushHostPurchase } from "../state/host-datalayer";
import { trackAdsConversion } from "../state/tracking";
import { WidgetLayer } from "./WidgetLayer";

// A leg that failed to book at finalize, paired with the original cart item so
// the resolver can re-drive availability + re-book it.
interface FailedLeg {
  index: number;
  item: ChideskBookItem;
}

type ResumeState =
  | { kind: "idle" }
  | { kind: "verifying"; pending: PendingChideskBooking; tries: number }
  | {
      kind: "booking";
      pending: PendingChideskBooking;
      /** What the deposit took (the payment row), for the Ads conversion. */
      paid?: { cents: number; currency: string };
    }
  | {
      kind: "resolve";
      pending: PendingChideskBooking;
      bookedRefs: string[];
      failed: FailedLeg[];
    }
  | {
      kind: "done";
      references: string[];
      rescheduledCount?: number;
      refundedCount?: number;
      refundOk?: boolean;
    }
  | { kind: "error"; message: string; recoverable: boolean };

const MAX_VERIFY_TRIES = 30; // ~45s at 1.5s intervals
const VERIFY_INTERVAL_MS = 1500;

function readReferenceFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return params.get("vt_chidesk_ref");
}

function stripReferenceFromUrl(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.delete("vt_chidesk_ref");
  url.searchParams.delete("reference"); // Paystack appends this too on return
  url.searchParams.delete("trxref");
  window.history.replaceState({}, "", url.toString());
}

// Human "09:00 AM" for a ChiDesk start. The start string is REAL UTC; display
// converts to tenant-local by adding the tenant's UTC offset.
function timeLabelOf(startUtc: string, tzOffsetMin: number): string {
  return labelFromWallMinutes(normMinute(wallMinutesOf(startUtc) + tzOffsetMin));
}

// Friendly TENANT-LOCAL day label from a UTC start — offset applied with day
// rollover (a 22:30Z start is the NEXT local day in SAST).
function dayLabelOf(startUtc: string, tzOffsetMin: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(startUtc);
  if (!m) return startUtc.slice(0, 10);
  const total = Number(m[4]) * 60 + Number(m[5]) + tzOffsetMin;
  const dayShift = Math.floor(total / 1440);
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + dayShift, 12);
  if (Number.isNaN(d.getTime())) return startUtc.slice(0, 10);
  return d.toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long" });
}

// Build the re-book item for a chosen replacement slot. Keeps the treatment,
// deposit, currency and "for someone else" contact; swaps in the new fake-Z
// start (+ textual end preserving the fake-Z wall clock). Therapist: a SPECIFIC
// original pick is kept as-is (availability was fetched for that therapist);
// "any available" narrows the candidate pool to whoever is free at the new slot.
function buildRebookItem(item: ChideskBookItem, slot: ChideskSlot): ChideskBookItem {
  const specific = item.chidesk_staff_guid ?? null;
  let candidates = item.chidesk_staff_candidates;
  if (!specific) {
    const free = (item.chidesk_staff_candidates ?? []).filter((c) =>
      slot.employeeIds.includes(c.guid),
    );
    candidates =
      free.length > 0
        ? free
        : slot.employeeIds.map((guid) => ({ guid, staff_row_id: null, name: null }));
  }
  return {
    ...item,
    starts_at: slot.start,
    ends_at: shiftIsoWallClock(slot.start, item.duration_minutes),
    chidesk_staff_candidates: specific ? item.chidesk_staff_candidates : candidates,
  };
}

export function ChideskResumeModal({ tenantSlug }: { tenantSlug: string }) {
  const [state, setState] = useState<ResumeState>({ kind: "idle" });
  // Guard against double-fire under React 18 StrictMode dev double-mount.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    const reference = readReferenceFromUrl();
    if (!reference) return;
    const pending = loadPendingChideskBooking(reference);
    if (!pending) {
      // No cart payload — the customer probably opened the URL on a
      // different device. Don't loop the modal; just clean up the URL.
      stripReferenceFromUrl();
      return;
    }
    startedRef.current = true;
    setState({ kind: "verifying", pending, tries: 0 });
  }, []);

  useEffect(() => {
    if (state.kind !== "verifying") return;
    let cancelled = false;

    const tick = async () => {
      const res = await getChideskPaymentStatus(state.pending.reference);
      if (cancelled) return;

      if (!res.ok) {
        setState({
          kind: "error",
          message:
            "We couldn't reach the payment service. Please refresh and try again.",
          recoverable: true,
        });
        return;
      }

      if (res.status === "success") {
        // Who owns the booking? finalize_status is null for deposits with no
        // server-side payload — every deposit before 20260817000010, and any
        // where the attach call didn't land — and those finalise here exactly
        // as they always have. Anything else means paystack-webhook claimed
        // it, and booking from here too would double-book: the proxy's replay
        // guard counts appointments, so two bookers arriving together both
        // read zero.
        const finalize = res.finalize_status ?? null;
        if (finalize === null) {
          setState({
            kind: "booking",
            pending: state.pending,
            paid: { cents: res.amount_cents, currency: res.currency },
          });
          return;
        }
        if (finalize === "booked") {
          // The usual case: the server booked every leg. The venue's own
          // tags (an embed on its site) hear of it with the amount paid,
          // once per payment reference — the server holds the appointment
          // references (state/host-datalayer.ts).
          pushHostPurchase({
            tenant: tenantSlug,
            reference: state.pending.reference,
            value: majorUnits(res.amount_cents),
            currency: res.currency || state.pending.items[0]?.currency || "ZAR",
          });
          // Its Google Ads conversion at the amount paid (GA4 and Meta: the
          // server's, from the deposit), on the payment reference.
          trackAdsConversion({
            reference: state.pending.reference,
            value: majorUnits(res.amount_cents) ?? 0,
            currency: res.currency || state.pending.items[0]?.currency || "ZAR",
            email: state.pending.primary.email,
          });
          clearPendingChideskBooking(state.pending.reference);
          stripReferenceFromUrl();
          // The server holds the per-leg references; it booked every leg, so
          // the confirmation renders its tenant-name fallback rather than a
          // list. Saying "booked" without them beats implying it failed.
          setState({ kind: "done", references: [] });
          return;
        }
        if (finalize === "partial") {
          clearPendingChideskBooking(state.pending.reference);
          stripReferenceFromUrl();
          setState({
            kind: "error",
            message:
              "Part of your spa booking went through and part of it didn't. The venue has been notified and will call you to sort out the rest — your deposit is safe.",
            recoverable: false,
          });
          return;
        }
        if (finalize === "refunded" || finalize === "failed") {
          clearPendingChideskBooking(state.pending.reference);
          stripReferenceFromUrl();
          setState({
            kind: "error",
            message:
              finalize === "refunded"
                ? "The spa couldn't take that time after all, so your deposit has been refunded — it'll be back on your card within a few days. Please pick another time."
                : "Your deposit was charged but the spa booking couldn't be completed. The venue must review the payment and verify your details and original card before processing any refund.",
            recoverable: false,
          });
          return;
        }
        // 'pending' / 'in_progress' — the webhook is mid-flight. Fall through
        // to the retry counter below and ask again.
      }
      if (res.status === "failed" || res.status === "cancelled") {
        clearPendingChideskBooking(state.pending.reference);
        stripReferenceFromUrl();
        setState({
          kind: "error",
          message:
            "Your payment didn't go through. No money was taken — please try again.",
          recoverable: false,
        });
        return;
      }

      const next = state.tries + 1;
      if (next >= MAX_VERIFY_TRIES) {
        // Two different waits end up here, and telling a guest whose payment
        // already succeeded that we're "waiting for Paystack" reads as though
        // their money is in limbo. When the server owns the finalize it will
        // finish (or refund) without this tab, so say that instead.
        setState({
          kind: "error",
          message:
            res.status === "success"
              ? "Your deposit went through and the spa booking is being confirmed now. You'll get a confirmation shortly — you can safely close this page."
              : "We're still waiting for Paystack to confirm your payment. Refresh this page in a moment to finalise your booking.",
          recoverable: true,
        });
        return;
      }
      setState({ kind: "verifying", pending: state.pending, tries: next });
    };

    const id = window.setTimeout(tick, state.tries === 0 ? 0 : VERIFY_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [state]);

  useEffect(() => {
    if (state.kind !== "booking") return;
    let cancelled = false;
    (async () => {
      const result = await bookChideskCart({
        tenantIntegrationId: state.pending.tenantIntegrationId,
        cartToken: state.pending.cartToken,
        primary: state.pending.primary,
        items: state.pending.items,
        paymentReference: state.pending.reference,
      });
      if (cancelled) return;
      clearPendingChideskBooking(state.pending.reference);
      stripReferenceFromUrl();

      if ("results" in result) {
        const bookedRefs = result.results
          .filter((r) => r.ok)
          .map((r) => r.reference)
          .filter((ref): ref is string => typeof ref === "string" && ref.length > 0);
        const failed: FailedLeg[] = result.results
          .filter((r) => !r.ok)
          .map((r) => ({ index: r.index, item: state.pending.items[r.index] }))
          .filter((l): l is FailedLeg => Boolean(l.item));
        // The venue's own tags (an embed on its site) hear of the treatments
        // booked now, at what they come to (amount_cents is the price for
        // one: × the item's quantity, as the checkout counts it), under the
        // first one's reference (state/host-datalayer.ts). Legs re-timed in
        // the resolver below book later, outside this push.
        const booked = result.results.filter((r) => r.ok && state.pending.items[r.index]);
        if (booked.length > 0) {
          pushHostPurchase({
            tenant: tenantSlug,
            reference: bookedRefs[0] ?? null,
            value: majorUnits(
              booked.reduce(
                (n, r) =>
                  n +
                  (state.pending.items[r.index]?.amount_cents ?? 0) *
                    (state.pending.quantities?.[r.index] || 1),
                0,
              ),
            ),
            currency: state.pending.items[booked[0]!.index]?.currency ?? "ZAR",
          });
          // Its Google Ads conversion at the amount the deposit took (GA4 and
          // Meta: the server's, from the payment), on the payment reference,
          // as the server-booked branch.
          trackAdsConversion({
            reference: state.pending.reference,
            value: majorUnits(state.paid?.cents) ?? 0,
            currency: state.paid?.currency || state.pending.items[booked[0]!.index]?.currency || "ZAR",
            email: state.pending.primary.email,
          });
        }

        if (failed.length === 0) {
          // Everything booked — the happy path.
          setState({ kind: "done", references: bookedRefs });
          return;
        }
        // Partial (or all-failed): hand the failed treatments back to the guest
        // to re-time or cancel+refund. Deposit is already paid; nothing here
        // charges again.
        setState({
          kind: "resolve",
          pending: state.pending,
          bookedRefs,
          failed,
        });
        return;
      }

      // No per-leg results at all — a pre-loop failure (e.g. the reference
      // couldn't be verified). The customer paid, so say the venue has the
      // deposit; the auto-refund flow can still reconcile on cancellation.
      setState({
        kind: "error",
        message:
          `Your deposit was charged successfully, but we couldn't finalise the spa booking (${result.error ?? "unknown error"}). The venue has your deposit on file and will reach out shortly.`,
        recoverable: false,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [state]);

  if (state.kind === "idle") return null;

  return (
    <WidgetLayer>
      <div className="vt-resume-backdrop">
        <div className="vt-resume-card" role="dialog" aria-modal="true">
          {(state.kind === "verifying" || state.kind === "booking") && (
            <>
              <div className="vt-resume-spinner" aria-hidden />
              <h3>Finalising your booking…</h3>
              <p className="vt-muted">
                {state.kind === "verifying"
                  ? "Confirming your payment with Paystack."
                  : "Reserving your treatments at the venue."}
              </p>
              <p className="vt-muted" style={{ fontSize: 11, marginTop: 12 }}>
                Please don't close this page.
              </p>
            </>
          )}
          {state.kind === "resolve" && (
            <PartialResolver
              pending={state.pending}
              bookedRefs={state.bookedRefs}
              failed={state.failed}
              onResolved={(summary) => setState({ kind: "done", ...summary })}
            />
          )}
          {state.kind === "done" && (
            <>
              <div className="vt-check">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h3>Booking confirmed</h3>
              <p className="vt-muted">
                Your treatments are reserved. A confirmation will be sent to your
                email.
              </p>
              {(state.refundedCount ?? 0) > 0 && (
                <p className="vt-muted">
                  {state.refundOk === false
                    ? `We couldn't submit the refund request for ${state.refundedCount} treatment(s). Please contact the venue to arrange a review.`
                    : `${state.refundedCount} treatment(s) you cancelled ${
                        state.refundedCount === 1 ? "has" : "have"
                      } been submitted for refund review. The venue must verify your details and original payment card before processing.`}
                </p>
              )}
              {state.references.length === 1 && (
                <p className="vt-muted">Your reference: {state.references[0]}</p>
              )}
              {state.references.length > 1 && (
                <p className="vt-muted">
                  Your references: {state.references.join(", ")}
                </p>
              )}
              <button
                type="button"
                className="vt-cta"
                onClick={() => setState({ kind: "idle" })}
              >
                Done
              </button>
            </>
          )}
          {state.kind === "error" && (
            <>
              <h3>Something went wrong</h3>
              <p>{state.message}</p>
              <button
                type="button"
                className="vt-cta"
                onClick={() => {
                  if (!state.recoverable) {
                    stripReferenceFromUrl();
                  }
                  setState({ kind: "idle" });
                }}
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </WidgetLayer>
  );
}

// ── Partial-booking resolver ─────────────────────────────────────────────────
// Guest-driven: for each treatment that couldn't be booked we show its LIVE
// available times (same day, honouring the venue's yield-management grid) and a
// cancel-and-refund choice. The guest picks per treatment; "Confirm" re-books
// the re-timed legs against the deposit already paid and issues ONE summed
// refund for any cancelled legs. Nothing here charges the card again.

type LegDecision =
  | { kind: "none" }
  | { kind: "reschedule"; slot: ChideskSlot }
  | { kind: "cancel" };

interface LegUi {
  index: number;
  item: ChideskBookItem;
  slots: ChideskDaySlots | "loading";
  decision: LegDecision;
  phase: "idle" | "working" | "booked" | "error";
  error?: string;
  bookedRef?: string | null;
}

interface ResolveSummary {
  references: string[];
  rescheduledCount: number;
  refundedCount: number;
  refundOk: boolean;
}

function PartialResolver({
  pending,
  bookedRefs,
  failed,
  onResolved,
}: {
  pending: PendingChideskBooking;
  bookedRefs: string[];
  failed: FailedLeg[];
  onResolved: (summary: ResolveSummary) => void;
}) {
  const [cfg, setCfg] = useState<ChideskBookingConfig | null>(null);
  const [legs, setLegs] = useState<LegUi[]>(() =>
    failed.map((f) => ({
      index: f.index,
      item: f.item,
      slots: "loading" as const,
      decision: { kind: "none" } as LegDecision,
      phase: "idle" as const,
    })),
  );
  const [busy, setBusy] = useState(false);

  // Venue yield-management grid (only-these-times) — filter alternatives to it.
  useEffect(() => {
    let cancelled = false;
    fetchChideskBookingConfig(pending.tenantIntegrationId).then((c) => {
      if (!cancelled) setCfg(c);
    });
    return () => {
      cancelled = true;
    };
  }, [pending.tenantIntegrationId]);

  // Load each failed leg's same-day availability once the tenant config (with
  // its tz offset) has resolved — slot labels must be tenant-local from the
  // first paint. The local DAY may differ from the UTC date prefix near
  // midnight, so derive it via the offset.
  const localDayOf = (utcStart: string, off: number): string => {
    const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(utcStart);
    if (!m) return utcStart.slice(0, 10);
    const total = Number(m[4]) * 60 + Number(m[5]) + off;
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + Math.floor(total / 1440)));
    return d.toISOString().slice(0, 10);
  };
  const loadedRef = useRef(false);
  useEffect(() => {
    if (!cfg || loadedRef.current) return;
    loadedRef.current = true;
    let cancelled = false;
    (async () => {
      const loaded = await Promise.all(
        failed.map((f) =>
          fetchChideskDaySlots({
            tenantIntegrationId: pending.tenantIntegrationId,
            treatmentGuid: f.item.chidesk_treatment_guid,
            day: localDayOf(f.item.starts_at, cfg.tzOffsetMinutes),
            staffGuid: f.item.chidesk_staff_guid ?? null,
            tzOffsetMinutes: cfg.tzOffsetMinutes,
          }),
        ),
      );
      if (cancelled) return;
      setLegs((prev) =>
        prev.map((l, i) => ({ ...l, slots: loaded[i] ?? { ok: false, error: "load_failed" } })),
      );
    })();
    return () => {
      cancelled = true;
    };
    // failed is fixed for the lifetime of this resolver instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg]);

  const grid = cfg?.bookingGrid ?? [];
  // Tenant UTC-offset for all label/day conversions in this resolver.
  const off = cfg?.tzOffsetMinutes ?? 120;
  const visibleSlots = (slots: ChideskDaySlots): ChideskSlot[] => {
    if (!slots.ok) return [];
    return slots.slots.filter((s) => grid.length === 0 || grid.includes(s.minuteOfDay));
  };

  const allDecided = legs.every((l) => l.decision.kind !== "none");

  const setLeg = (index: number, patch: Partial<LegUi>) =>
    setLegs((prev) => prev.map((l) => (l.index === index ? { ...l, ...patch } : l)));

  async function handleConfirm() {
    setBusy(true);
    let hadError = false;
    const rebooked: string[] = [];
    // Work on a mutable snapshot; re-book each re-timed leg one at a time.
    const working = [...legs];
    for (let li = 0; li < working.length; li++) {
      const leg = working[li]!;
      if (leg.decision.kind !== "reschedule") continue;
      setLeg(leg.index, { phase: "working", error: undefined });
      const slot = leg.decision.slot;
      const res = await bookChideskCart({
        tenantIntegrationId: pending.tenantIntegrationId,
        cartToken: pending.cartToken,
        primary: pending.primary,
        items: [buildRebookItem(leg.item, slot)],
        paymentReference: pending.reference,
        resolvePending: true,
      });
      const legResult =
        "results" in res && res.results.length > 0 ? res.results[0] : null;
      if (legResult?.ok) {
        const ref = legResult.reference ?? null;
        if (ref) rebooked.push(ref);
        working[li] = { ...leg, phase: "booked", bookedRef: ref };
        setLeg(leg.index, { phase: "booked", bookedRef: ref });
      } else {
        hadError = true;
        const msg =
          legResult?.error ??
          ("error" in res ? res.error : undefined) ??
          "That time was just taken — please choose another.";
        // Re-fetch this leg's availability so the guest re-picks from fresh
        // times (the slot they chose is now gone).
        const fresh = await fetchChideskDaySlots({
          tenantIntegrationId: pending.tenantIntegrationId,
          treatmentGuid: leg.item.chidesk_treatment_guid,
          day: localDayOf(leg.item.starts_at, off),
          staffGuid: leg.item.chidesk_staff_guid ?? null,
          tzOffsetMinutes: off,
        });
        working[li] = {
          ...leg,
          phase: "error",
          error: msg,
          decision: { kind: "none" },
          slots: fresh,
        };
        setLeg(leg.index, {
          phase: "error",
          error: msg,
          decision: { kind: "none" },
          slots: fresh,
        });
      }
    }

    if (hadError) {
      // Leave the resolver open on the errored leg(s); the guest re-picks.
      setBusy(false);
      return;
    }

    // Refund all cancelled legs in ONE call (keeps the proxy's list-first
    // idempotency correct).
    const cancelLegs = working.filter((l) => l.decision.kind === "cancel");
    const cancelSum = cancelLegs.reduce((s, l) => s + (l.item.deposit_cents ?? 0), 0);
    let refundOk = true;
    if (cancelSum > 0) {
      const r = await refundPendingLegs({
        tenantIntegrationId: pending.tenantIntegrationId,
        paymentReference: pending.reference,
        depositCents: cancelSum,
        cartToken: pending.cartToken,
      });
      refundOk = r.ok;
    }
    setBusy(false);
    onResolved({
      references: [...bookedRefs, ...rebooked],
      rescheduledCount: rebooked.length,
      refundedCount: cancelLegs.length,
      refundOk,
    });
  }

  return (
    <div className="vt-text-left" style={{ maxHeight: "70vh", overflowY: "auto", width: "100%" }}>
      <h3 className="vt-text-center">Let's finish your booking</h3>
      <p className="vt-muted vt-text-center">
        {bookedRefs.length > 0
          ? "Your deposit went through and most of your treatments are booked. One or more couldn't be secured at the time you chose — please pick another time or cancel it for a refund."
          : "Your deposit went through, but the time you chose was just taken. Please pick another time below, or cancel for a refund."}
      </p>

      {legs.map((leg) => {
        const slots = leg.slots;
        const options = slots === "loading" ? [] : visibleSlots(slots);
        return (
          <section
            key={leg.index}
            style={{
              border: "1px solid #e5e0d3",
              borderRadius: 12,
              padding: 14,
              marginTop: 12,
              background: "#fff",
            }}
          >
            <div style={{ fontWeight: 600 }}>{leg.item.treatment_name}</div>
            <div className="vt-muted" style={{ fontSize: 12, marginBottom: 8 }}>
              Couldn't be booked for {timeLabelOf(leg.item.starts_at, off)} on{" "}
              {dayLabelOf(leg.item.starts_at, off)}.
            </div>

            {leg.phase === "booked" && leg.decision.kind === "reschedule" && (
              <div
                style={{
                  background: "#e8f5ec",
                  color: "#2d7d46",
                  borderRadius: 8,
                  padding: "8px 10px",
                  fontSize: 13,
                }}
              >
                ✓ Rebooked for {timeLabelOf(leg.decision.slot.start, off)}
                {leg.bookedRef ? ` (ref: ${leg.bookedRef})` : ""}
              </div>
            )}

            {leg.phase !== "booked" && (
              <>
                {leg.error && (
                  <div
                    style={{
                      background: "#fdecec",
                      color: "#b23b3b",
                      borderRadius: 8,
                      padding: "8px 10px",
                      fontSize: 13,
                      marginBottom: 8,
                    }}
                  >
                    {leg.error}
                  </div>
                )}

                {slots === "loading" && (
                  <div className="vt-muted" style={{ fontSize: 13 }}>
                    Loading available times…
                  </div>
                )}

                {slots !== "loading" && (
                  <>
                    <div className="vt-muted" style={{ fontSize: 12, marginBottom: 6 }}>
                      Available times · {dayLabelOf(leg.item.starts_at, off)}
                    </div>
                    {options.length === 0 ? (
                      <div className="vt-muted" style={{ fontSize: 13, marginBottom: 8 }}>
                        No other times are available that day. You can cancel this
                        treatment for a refund.
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                        {options.map((s) => {
                          const selected =
                            leg.decision.kind === "reschedule" &&
                            leg.decision.slot.start === s.start;
                          return (
                            <button
                              key={s.start}
                              type="button"
                              disabled={busy}
                              onClick={() =>
                                setLeg(leg.index, {
                                  decision: { kind: "reschedule", slot: s },
                                  error: undefined,
                                })
                              }
                              style={{
                                border: selected ? "2px solid #2d7d46" : "1px solid #d8d2c4",
                                background: selected ? "#e8f5ec" : "#fff",
                                borderRadius: 8,
                                padding: "6px 12px",
                                fontSize: 13,
                                cursor: busy ? "default" : "pointer",
                                fontWeight: selected ? 600 : 400,
                              }}
                            >
                              {s.label}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 13,
                        cursor: busy ? "default" : "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        disabled={busy}
                        checked={leg.decision.kind === "cancel"}
                        onChange={(e) =>
                          setLeg(leg.index, {
                            decision: e.target.checked
                              ? { kind: "cancel" }
                              : { kind: "none" },
                            error: undefined,
                          })
                        }
                      />
                      Cancel this treatment &amp; request a refund
                      {(leg.item.deposit_cents ?? 0) > 0
                        ? ` (${leg.item.currency} ${((leg.item.deposit_cents ?? 0) / 100).toFixed(2)})`
                        : ""}
                    </label>
                  </>
                )}
              </>
            )}
          </section>
        );
      })}

      <button
        type="button"
        className="vt-cta"
        disabled={busy || !allDecided}
        style={{ marginTop: 16, width: "100%", opacity: busy || !allDecided ? 0.55 : 1 }}
        onClick={handleConfirm}
      >
        {busy ? "Working…" : "Confirm"}
      </button>
      {!allDecided && (
        <p className="vt-muted vt-text-center" style={{ fontSize: 11, marginTop: 8 }}>
          Choose a new time or select cancel for each treatment above.
        </p>
      )}
    </div>
  );
}
